'use client';

import React from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react';
import { LuArrowLeft } from 'react-icons/lu';

import { Breadcrumbs, BreadcrumbsProps } from './breadcrumbs';

export interface PageLayoutProps {
  /**
   * Page title
   */
  title: string;
  /**
   * Optional subtitle or description
   */
  subtitle?: string;
  /**
   * Breadcrumb items (optional, can be auto-generated from route)
   */
  breadcrumbs?: BreadcrumbsProps['items'];
  /**
   * Action buttons to display in the header
   */
  actions?: React.ReactNode;
  /**
   * Main content
   */
  children: React.ReactNode;
  /**
   * Whether to show a back button
   */
  showBackButton?: boolean;
  /**
   * Back button text
   */
  backButtonText?: string;
  /**
   * Callback when back button is clicked
   */
  onBack?: () => void;
  /**
   * Loading state
   */
  isLoading?: boolean;
  /**
   * Error state
   */
  error?: Error | null;
  /**
   * Empty state component
   */
  emptyState?: React.ReactNode;
  /**
   * Whether the content has data
   */
  hasData?: boolean;
  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode;
  /**
   * Custom error component
   */
  errorComponent?: React.ReactNode;
  /**
   * Content padding (responsive)
   */
  contentPadding?: string | number | Record<string, string | number>;
  /**
   * Maximum content width
   */
  maxContentWidth?: string;
  /**
   * Whether to center the content
   */
  centerContent?: boolean;
  /**
   * Additional header content
   */
  headerExtra?: React.ReactNode;
  /**
   * i18n translations
   */
  translations?: {
    loading?: string;
    error?: string;
    backToHome?: string;
    retryAction?: string;
  };
}

export function PageLayout({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
  showBackButton = false,
  backButtonText,
  onBack,
  isLoading = false,
  error = null,
  emptyState,
  hasData = true,
  loadingComponent,
  errorComponent,
  contentPadding = { base: 4, md: 6, lg: 8 },
  maxContentWidth = '1400px',
  centerContent = true,
  headerExtra,
  translations = {},
}: PageLayoutProps) {
  const _isMobile = useBreakpointValue({ base: true, md: false });

  // Default translations
  const t = {
    loading: translations.loading || 'Loading...',
    error: translations.error || 'An error occurred',
    backToHome: translations.backToHome || 'Back to home',
    retryAction: translations.retryAction || 'Try again',
  };

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        loadingComponent || (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
            <Text color="#6C757D">{t.loading}</Text>
          </Box>
        )
      );
    }

    if (error) {
      return (
        errorComponent || (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
            <VStack gap={4}>
              <Text color="#DC3545">{error.message || t.error}</Text>
              <Button
                colorPalette="blue"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                {t.retryAction}
              </Button>
            </VStack>
          </Box>
        )
      );
    }

    if (!hasData && emptyState) {
      return emptyState;
    }

    return children;
  };

  return (
    <Box minHeight="100vh" backgroundColor="#F8F9FA">
      {/* Page Header */}
      <Box
        backgroundColor="white"
        borderBottomWidth="1px"
        borderColor="#E9ECEF"
        position="sticky"
        top="0"
        zIndex="sticky"
      >
        <Box
          maxWidth={maxContentWidth}
          marginX={centerContent ? 'auto' : undefined}
          paddingX={contentPadding}
        >
          {/* Breadcrumbs */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Box paddingTop={4}>
              <Breadcrumbs items={breadcrumbs} />
            </Box>
          )}

          {/* Title Section */}
          <Flex
            paddingY={4}
            alignItems="start"
            justifyContent="space-between"
            flexDirection="column"
            gap={4}
          >
            <VStack align="start" gap={2} flex="1">
              <HStack gap={3} align="center">
                {showBackButton && (
                  <Button variant="ghost" size="sm" onClick={onBack}>
                    <LuArrowLeft />
                    {backButtonText || t.backToHome}
                  </Button>
                )}
                <Heading as="h1" size="lg">
                  {title}
                </Heading>
              </HStack>
              {subtitle && (
                <Text color="#6C757D" fontSize="sm">
                  {subtitle}
                </Text>
              )}
            </VStack>

            {/* Actions */}
            {actions && (
              <HStack gap={2} wrap="wrap" justifyContent="start">
                {actions}
              </HStack>
            )}
          </Flex>

          {/* Extra Header Content */}
          {headerExtra && (
            <Box paddingBottom={4} borderTopWidth="1px" borderColor="#E9ECEF" paddingTop={4}>
              {headerExtra}
            </Box>
          )}
        </Box>
      </Box>

      {/* Page Content */}
      <Box
        flex="1"
        paddingY={contentPadding}
        paddingX={contentPadding}
        maxWidth={maxContentWidth}
        marginX={centerContent ? 'auto' : undefined}
        width="100%"
      >
        {renderContent()}
      </Box>
    </Box>
  );
}

// Export with display name for debugging
PageLayout.displayName = 'PageLayout';
